// User role definitions
export enum UserRole {
  ADMIN = "ADMIN",
  RESEARCHER = "RESEARCHER",
  CLIENT = "CLIENT", // Can be renamed to 'VIEWER' if needed
  VIEWER = "VIEWER", // Alternative name for client
}

// User interface with role information
export interface UserWithRole {
  id?: string;
  name?: string;
  email?: string;
  email_verified?: boolean;
  role: UserRole;
  permissions?: string[];
  // Add other user properties as needed
}

// Define resource types
export type Resource =
  | "dashboard"
  | "survey"
  | "dataset"
  | "user"
  | "settings"
  | "report"
  | "analytics"
  | "export"
  | "import"
  | "template"
  | "notification"
  | "integration"
  | "api"
  | "audit"
  | "backup";

// Define action types
export type Action =
  | "create"
  | "read"
  | "update"
  | "delete"
  | "share"
  | "export"
  | "import"
  | "manage"
  | "configure"
  | "publish"
  | "archive"
  | "restore";

// Permission type combining resource and action
export type Permission = `${Resource}:${Action}`;

// Define role capabilities using a more flexible approach
export interface RoleCapabilities {
  // Full access to all resources and actions
  fullAccess?: boolean;

  // Resources this role can access
  resources: Resource[];

  // Actions this role can perform (applies to all accessible resources)
  actions: Action[];

  // Specific permissions (overrides general resource/action rules)
  specificPermissions?: Permission[];

  // Denied permissions (explicitly denied even if would be allowed by resources/actions)
  deniedPermissions?: Permission[];

  // Special capabilities
  canManageUsers?: boolean;
  canAccessSettings?: boolean;
  canViewAuditLogs?: boolean;
  canExportData?: boolean;
  canImportData?: boolean;
}

// Role-based capabilities mapping
export const ROLE_CAPABILITIES: Record<UserRole, RoleCapabilities> = {
  [UserRole.ADMIN]: {
    fullAccess: true,
    resources: [], // Not needed when fullAccess is true
    actions: [], // Not needed when fullAccess is true
    canManageUsers: true,
    canAccessSettings: true,
    canViewAuditLogs: true,
    canExportData: true,
    canImportData: true,
  },

  [UserRole.RESEARCHER]: {
    resources: [
      "dashboard",
      "survey",
      "dataset",
      "report",
      "analytics",
      "template",
      "export",
      "import",
    ],
    actions: [
      "create",
      "read",
      "update",
      "delete",
      "share",
      "export",
      "import",
      "manage",
      "publish",
      "archive",
    ],
    specificPermissions: ["settings:read"], // Can view settings but not manage
    canAccessSettings: false, // Can view but not manage
    canExportData: true,
    canImportData: true,
  },

  [UserRole.CLIENT]: {
    resources: ["dashboard", "survey", "dataset", "report", "analytics"],
    actions: ["read", "export"], // Read-only access with export capability
    canExportData: true,
    canImportData: false,
  },

  [UserRole.VIEWER]: {
    resources: ["dashboard", "survey", "dataset", "report", "analytics"],
    actions: ["read", "export"], // Same as client
    canExportData: true,
    canImportData: false,
  },
};

// Route access mapping (unchanged as it's already concise)
export const ROLE_ROUTE_ACCESS: Record<UserRole, string[]> = {
  [UserRole.ADMIN]: [
    "/dashboard",
    "/surveys",
    "/datasets",
    "/settings",
    "/support",
    "/analytics",
    "/reports",
    "/users",
    "/audit",
  ],
  [UserRole.RESEARCHER]: [
    "/dashboard",
    "/surveys",
    "/datasets",
    "/settings",
    "/support",
    "/analytics",
    "/reports",
  ],
  [UserRole.CLIENT]: ["/dashboard"],
  [UserRole.VIEWER]: ["/dashboard"],
};

// Helper type for creating permissions dynamically
export const createPermission = (
  resource: Resource,
  action: Action,
): Permission => `${resource}:${action}` as Permission;

// Common permission sets for easy reuse
export const PERMISSION_SETS = {
  READ_ONLY: ["read"] as Action[],
  BASIC_CRUD: ["create", "read", "update", "delete"] as Action[],
  FULL_CRUD: [
    "create",
    "read",
    "update",
    "delete",
    "share",
    "export",
    "import",
  ] as Action[],
  ADMIN_ACTIONS: [
    "create",
    "read",
    "update",
    "delete",
    "share",
    "export",
    "import",
    "manage",
    "configure",
    "publish",
    "archive",
    "restore",
  ] as Action[],
} as const;

// Utility type for getting all possible permissions for a resource
export type ResourcePermissions<T extends Resource> = `${T}:${Action}`;
